import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';

type Drawer = 'cart' | 'menu' | null;

export interface ToastData {
  id: number;
  message: string;
  tone: 'ok' | 'error';
  action?: { label: string; onClick: () => void };
}

interface UIState {
  drawer: Drawer;
  openDrawer: (d: Exclude<Drawer, null>) => void;
  closeDrawer: () => void;
  toast: ToastData | null;
  showToast: (message: string, opts?: { tone?: 'ok' | 'error'; action?: ToastData['action'] }) => void;
  hideToast: () => void;
  /** Set by the rail while a garment is open, so the header can show "Close". */
  focusClose: (() => void) | null;
  setFocusClose: (fn: (() => void) | null) => void;
}

const Ctx = createContext<UIState | null>(null);

export function UIProvider({ children }: { children: ReactNode }) {
  const [drawer, setDrawer] = useState<Drawer>(null);
  const [toast, setToast] = useState<ToastData | null>(null);
  const [focusClose, setFocusCloseState] = useState<(() => void) | null>(null);
  const counter = useRef(0);

  const showToast = useCallback<UIState['showToast']>((message, opts) => {
    counter.current += 1;
    setToast({ id: counter.current, message, tone: opts?.tone ?? 'ok', action: opts?.action });
  }, []);

  const value = useMemo<UIState>(() => ({
    drawer,
    openDrawer: (d) => setDrawer(d),
    closeDrawer: () => setDrawer(null),
    toast,
    showToast,
    hideToast: () => setToast(null),
    focusClose,
    // Functions must be wrapped, or React treats them as state updaters.
    setFocusClose: (fn) => setFocusCloseState(() => fn),
  }), [drawer, toast, showToast, focusClose]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useUI() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useUI must be used inside <UIProvider>');
  return v;
}
